/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { Page, Text, View, Link } from "@react-pdf/renderer";
import DataController from "@controllers/DataController";
import * as PDFStyles from "./PDFStyles";
import * as EvidenceUtils from "@utils/evidenceUtils";
import * as ProgramUtils from "@utils/programUtils";
import * as StringUtils from "@utils/stringUtils";
import { IPDFEvidence } from "./PDFTypes";
import {
  CompetenceActivityClass,
  EvidenceAttachment,
  EvidencePart,
  EvidenceStandardTag,
  FieldType,
  LearningObjective,
  ProgramData,
  ProgramSkill,
} from "@interfaces/Interfaces";
import _ from "lodash";
import { CreateEvidencePart } from "@components/programs/evidenceParts/ComponentWrapper";
import { format } from "date-fns";
import AWSService from "@controllers/AWSService";
import { Html } from "react-pdf-html";

const PDFEvidence: React.FC<IPDFEvidence> = (props) => {
  const getLearningOutcomesForEvidencePart = (params: {
    progressCheckCompetence: CompetenceActivityClass | undefined;
    program: ProgramData | undefined;
  }): LearningObjective[] => {
    const { progressCheckCompetence, program } = params;

    if (program && ProgramUtils.getLearningOutcomesFromRole(program?.ID)) {
      let userRoles: string[] = [];
      const programRoles = props.allUserRoles.find((role) => role.programID === program?.ID);

      if (programRoles) {
        userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
      }

      return ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]);
    }

    if (progressCheckCompetence) {
      return progressCheckCompetence["Learning Objective"] ?? [];
    }

    return [];
  };

  return (
    <>
      {props.evidence.map((item, index) => {
        const attachments: EvidenceAttachment[] = typeof item.attachments === "object" ? item.attachments : [];

        const images = attachments.filter(
          (attachment) =>
            attachment.type === "image/jpg" || attachment.type === "image/jpeg" || attachment.type === "image/png"
        );

        const nonImageAttachments = attachments.filter(
          (attachment) =>
            attachment.type !== "image/jpg" && attachment.type !== "image/jpeg" && attachment.type !== "image/png"
        );

        let evidenceJSON: any;

        if (typeof item.evidenceJSON === "string") {
          evidenceJSON = JSON.parse(item.evidenceJSON);
        } else if (typeof item.evidenceJSON === "object") {
          evidenceJSON = item.evidenceJSON;
        }

        const sortedAttachments = _.concat(images, nonImageAttachments);
        const title: string = (item?.title || evidenceJSON?.Title) ?? "";

        let evidenceParts: EvidencePart[] = [];
        let _program: ProgramData | undefined;
        let progressCheckCompetence: CompetenceActivityClass | undefined;
        let skills: ProgramSkill[] = [];

        if (evidenceJSON) {
          const competenceName = evidenceJSON.programInfo?.competence;
          const programName = evidenceJSON.programInfo?.program;
          const progressCheckID = evidenceJSON.programInfo?.progressCheckID;
          const progressCheckCompetenceID = evidenceJSON.programInfo?.progressCheckCompetenceID;

          _program = props.userPrograms?.find((item: any): item is ProgramData => item && item.Name === programName);

          const _competences: any = _program?.CompetenceActivity ?? [];
          const _progressChecks: any[] = _program?.ProgressChecks ?? [];

          const competence = _competences?.find(
            (item: any): item is CompetenceActivityClass => item.Name === competenceName
          );

          skills = ProgramUtils.getSkillsForRole(props.user, _program!);

          const evidenceDefinitions = _program?.["Evidence Definitions"] ?? [];
          const progressCheck = _progressChecks && _progressChecks?.find((item: any) => item.ID === progressCheckID);

          const usesAdditionalEvidenceDefinitions = _program?.UsesAdditionalEvidenceDefinitions;

          const evidenceType = ProgramUtils.getEvidenceType({
            evidenceJSON: evidenceJSON,
            usesAdditionalEvidenceDefinitions: usesAdditionalEvidenceDefinitions ?? false,
            evidenceDefinitions,
            formEvidenceDefinitions:
              _program?.["WPBA Forms"]?.flatMap((form) => form["Evidence Definitions"] ?? []) ?? [],
          });

          if (evidenceType === "skills") {
            const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "skills");

            evidenceParts = evidenceDefinition?.["Evidence parts"] ?? [];
          } else if (evidenceType === "capabilities") {
            const evidenceDefinition = evidenceDefinitions.find(
              (item) => item.EvidenceSelectionType === "capabilities"
            );

            evidenceParts = evidenceDefinition?.["Evidence parts"] ?? [];
          } else if (evidenceType === "medical-imaging") {
            const evidenceDefinition = evidenceDefinitions.find(
              (item) => item.EvidenceSelectionType === "medical-imaging"
            );

            evidenceParts = evidenceDefinition?.["Evidence parts"] ?? [];
          } else if (evidenceType === "wpba-supervision-form") {
            const wpbaForms = _program?.["WPBA Forms"] ?? [];
            const formEvidenceDefinitions = wpbaForms.flatMap((form) => form["Evidence Definitions"]?.[0] ?? []);

            const evidenceDefinitions = formEvidenceDefinitions.find(
              (item) => item.ID === evidenceJSON.programInfo.programEvidenceDefinitionID
            );

            evidenceParts = evidenceDefinitions?.["Evidence parts"] ?? [];
          } else if (competence) {
            const evidenceDefinitions = competence["Evidence Definitions"][0];
            evidenceParts = evidenceDefinitions?.["Evidence parts"] ?? [];
          } else if (progressCheck) {
            const progressCheckCompetences = progressCheck["Competence/Activity"];
            progressCheckCompetence = progressCheckCompetences.find(
              (item: any): item is CompetenceActivityClass => item.ID === progressCheckCompetenceID
            );

            const evidenceDefinitions = progressCheckCompetence?.["Evidence Definitions"][0];
            evidenceParts = evidenceDefinitions ? EvidenceUtils.getEvidenceParts(evidenceDefinitions) : [];

            // console.log("evidenceDefinitions", evidenceDefinitions);
          } else {
            const evidenceDefinitions = usesAdditionalEvidenceDefinitions
              ? _program?.["Evidence Definitions"]?.find(
                  (item) => item.ID === evidenceJSON.programInfo?.programEvidenceDefinitionID
                )
              : _program?.["Evidence Definitions"]?.[0];

            evidenceParts = evidenceDefinitions?.["Evidence parts"] ?? [];
          }
        }

        const flattenedEvidenceParts = _.uniqBy(EvidenceUtils.flattenEvidenceParts(evidenceParts), "Name");

        return (
          <Page
            key={item.id}
            size="A4"
            style={PDFStyles.styles.page}
          >
            <View
              style={PDFStyles.styles.heading}
              fixed
            >
              <Text style={PDFStyles.styles.headingText}>{"ParaFolio"}</Text>
            </View>
            <View
              fixed
              style={PDFStyles.styles.pageHeader}
              render={({ pageNumber }) => (
                <>
                  <Text style={PDFStyles.styles.pageHeaderText}>{"Evidence"}</Text>
                  <Text style={PDFStyles.styles.pageNumber}>{pageNumber - 1}</Text>
                </>
              )}
            />
            <View style={PDFStyles.styles.pageInnerContainer}>
              {index === 0 && (
                <View>
                  <Text style={PDFStyles.styles.standardsHeaderOverview}>{"Evidence"}</Text>
                  <Text style={PDFStyles.styles.standardsHeaderDescription}>
                    {"Evidence is displayed by date, with the most recent evidence first."}
                  </Text>
                  <View style={PDFStyles.styles.separator} />
                </View>
              )}
              <Text style={PDFStyles.styles.evidenceTitle}>
                {title}
                {item.draft && <Text style={PDFStyles.styles.evidenceDraft}>{" (Draft)"}</Text>}
              </Text>
              {flattenedEvidenceParts?.length > 0 ? (
                <View>
                  {item.date && (
                    <View>
                      <Text style={PDFStyles.styles.reflectionHeader}>{"Date:"}</Text>
                      <Text style={PDFStyles.styles.evidenceDate}>{format(new Date(item.date), "d MMMM yyyy")}</Text>
                    </View>
                  )}
                  {flattenedEvidenceParts.map((part: any) => {
                    let dataForPart;
                    let mandatoryStandards;
                    let optionalStandards;

                    if (part["Field type"] === FieldType.NOSSelect) {
                      dataForPart = {
                        Comps: evidenceJSON.Comps,
                        LOs: evidenceJSON.LOs,
                      };

                      mandatoryStandards = ProgramUtils.getMandatoryStandards(props.user, _program!);
                      optionalStandards = ProgramUtils.getOptionalStandards(props.user, _program!);
                    } else {
                      dataForPart = evidenceJSON[part.Name];
                    }

                    if (dataForPart) {
                      return (
                        <View key={part.Name}>
                          {CreateEvidencePart(part["Field type"], {
                            definition: part,
                            data: dataForPart,
                            isPDF: true,
                            pdfStyles: PDFStyles.styles,
                            evidenceID: item.id,
                            program: props.program,
                            user: props.user,
                            accessParams: props.accessParams,
                            mandatoryStandards: mandatoryStandards,
                            optionalStandards: optionalStandards,
                            learningOutcomes: getLearningOutcomesForEvidencePart({
                              progressCheckCompetence,
                              program: _program,
                            }),
                            skills: skills,
                          })}
                        </View>
                      );
                    } else {
                      return null;
                    }
                  })}
                </View>
              ) : (
                <View>
                  <View style={PDFStyles.styles.evidenceDateRow}>
                    <Text style={PDFStyles.styles.evidenceDateBold}>{"Date of reflection:"}</Text>
                    <Text style={PDFStyles.styles.evidenceDate}>{format(new Date(item.date), "d MMMM yyyy")}</Text>
                  </View>
                  {item.activityDate && (
                    <View style={PDFStyles.styles.evidenceDateRow}>
                      <Text style={PDFStyles.styles.evidenceDateBold}>{"Date of activity:"}</Text>
                      <Text style={PDFStyles.styles.evidenceDate}>
                        {StringUtils.getActivityDateString(item.activityDate, item.activityDateEnd)}
                      </Text>
                    </View>
                  )}
                  {item.activity && (
                    <View style={PDFStyles.styles.evidenceDateRow}>
                      <Text style={PDFStyles.styles.evidenceDateBold}>{"Activity:"}</Text>
                      <Text style={PDFStyles.styles.evidenceDate}>{item.activity.type}</Text>
                    </View>
                  )}
                  {item.activityDescription && (
                    <View style={PDFStyles.styles.evidenceDateRow}>
                      <Text style={PDFStyles.styles.evidenceDateBold}>{"Activity description:"}</Text>
                      <Text style={PDFStyles.styles.evidenceDate}>{item.activityDescription}</Text>
                    </View>
                  )}
                  {item.numberOfHours && (
                    <View style={PDFStyles.styles.evidenceDateRow}>
                      <Text style={PDFStyles.styles.evidenceDateBold}>{"Number of hours:"}</Text>
                      <Text style={PDFStyles.styles.evidenceDate}>
                        {StringUtils.getActivityDurationDisplayString(
                          DataController.convertToHoursMinutes(item.numberOfHours)
                        )}
                      </Text>
                    </View>
                  )}
                  {!props.isHCPC && item.standardTags && item.standardTags.length > 0 && (
                    <View style={[PDFStyles.styles.evidenceDateRow, { marginBottom: 0 }]}>
                      <Text style={PDFStyles.styles.evidenceDateBold}>{"NOS met:"}</Text>
                      <Text style={PDFStyles.styles.evidenceDate}>
                        {item.standardTags && item.standardTags.length > 0
                          ? item.standardTags.map((tag: EvidenceStandardTag) => tag.tag).join(", ")
                          : "None"}
                      </Text>
                    </View>
                  )}
                </View>
              )}
              {props.accessParams && attachments.length > 0 && (
                <View>
                  <Text style={PDFStyles.styles.reflectionHeader}>{"Relevant documents:"}</Text>
                  {sortedAttachments.length > 0 &&
                    sortedAttachments.map((attachment: EvidenceAttachment) => {
                      const signedURL = AWSService.getAttachmentURL(
                        props.user,
                        props.accessParams,
                        item.id,
                        attachment
                      );

                      return (
                        <Link
                          style={PDFStyles.styles.hyperlink}
                          src={signedURL}
                          key={attachment.name}
                        >
                          {attachment.name}
                        </Link>
                      );
                    })}
                </View>
              )}
              <View>
                {item.reflections && (
                  <View>
                    <Text style={PDFStyles.styles.reflectionHeader}>{"Reflection:"}</Text>
                    <Html
                      style={{ fontSize: 12 }}
                      stylesheet={PDFStyles.htmlStyles}
                    >
                      {DataController.decodeHTML(item.reflections)}
                    </Html>
                  </View>
                )}
                {item.benefits && (
                  <View>
                    <Text style={PDFStyles.styles.reflectionHeader}>{"Benefits:"}</Text>
                    <Html
                      style={{ fontSize: 12 }}
                      stylesheet={PDFStyles.htmlStyles}
                    >
                      {DataController.decodeHTML(item.benefits)}
                    </Html>
                  </View>
                )}
                {item.objectives && (
                  <View>
                    <Text style={PDFStyles.styles.reflectionHeader}>{"Learning/ Exercise Objectives:"}</Text>
                    <Html
                      style={{ fontSize: 12 }}
                      stylesheet={PDFStyles.htmlStyles}
                    >
                      {DataController.decodeHTML(item.objectives)}
                    </Html>
                  </View>
                )}
              </View>
            </View>
          </Page>
        );
      })}
    </>
  );
};

export default PDFEvidence;
